import { useQuery } from '@tanstack/react-query';

// Imported directly until `referralApi` is exported from src/api/index.js.
import { referralApi } from '@/api';
import { RailCard } from '@/components/shell';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  ErrorState,
  Icon,
  Input,
  Skeleton,
  useToast,
} from '@/components/ui';
import { errorMessage } from '@/hooks/useApiError';
import { paths } from '@/routes/paths';
import { cn } from '@/utils/cn';
import { relativeTime } from '@/utils/format';

const SHARE_TEXT = 'I use Join-Wonder to find internships. Join me here:';
const SHARE_SUBJECT = 'Find internships with me on Join-Wonder';

function shareLinks(url) {
  const message = `${SHARE_TEXT} ${url}`;
  return [
    {
      key: 'whatsapp',
      label: 'WhatsApp',
      icon: 'chat',
      href: `https://wa.me/?text=${encodeURIComponent(message)}`,
    },
    {
      key: 'linkedin',
      label: 'LinkedIn',
      icon: 'linkedin',
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
    },
    {
      key: 'email',
      label: 'Email',
      icon: 'send',
      href: `mailto:?subject=${encodeURIComponent(SHARE_SUBJECT)}&body=${encodeURIComponent(message)}`,
    },
  ];
}

/** A small tinted tile that opens a share target in a new tab. */
function ShareLink({ link, compact }) {
  const external = !link.href.startsWith('mailto:');
  return (
    <a
      href={link.href}
      target={external ? '_blank' : undefined}
      rel={external ? 'noopener noreferrer' : undefined}
      className={cn(
        'border-line bg-surface text-ink hover:border-brand-200 hover:bg-brand-50/60 inline-flex items-center justify-center gap-1.5 rounded-lg border font-medium shadow-xs transition',
        compact ? 'h-8 px-2 text-xs' : 'h-9 px-3 text-sm',
      )}
    >
      <Icon name={link.icon} size={15} className="text-brand-600" />
      {link.label}
    </a>
  );
}

/**
 * The link to hand out, on the site the student is using right now.
 *
 * The backend's `invite_url` is built from its FRONTEND_URL setting, which
 * falls back to localhost when a server is not configured -- so a deployed
 * site handed out links nobody could open. The code is what matters, and
 * this origin is by definition one that works.
 */
function inviteUrl(data) {
  if (!data.code || typeof window === 'undefined') return data.invite_url;
  const url = new URL(paths.registerStudent, window.location.origin);
  url.searchParams.set('ref', data.code);
  return url.toString();
}

function InviteBody({ data: summary, compact }) {
  const toast = useToast();
  const canShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';
  const data = { ...summary, invite_url: inviteUrl(summary) };

  async function copy() {
    try {
      await navigator.clipboard.writeText(data.invite_url);
      toast.success('Invite link copied');
    } catch {
      toast.error('Could not copy. Select the link and copy it yourself.');
    }
  }

  async function share() {
    try {
      await navigator.share({ title: 'Join-Wonder', text: SHARE_TEXT, url: data.invite_url });
    } catch {
      // Closing the share sheet rejects too; nothing to tell the user.
    }
  }

  const joined = data.joined_count;
  const recent = compact ? data.recent.slice(0, 3) : data.recent;

  return (
    <div className="@container space-y-4">
      <div className="flex gap-2">
        <Input
          label="Your invite link"
          hideLabel
          readOnly
          size="sm"
          value={data.invite_url}
          onFocus={(event) => event.target.select()}
          wrapperClassName="min-w-0 flex-1"
          className="text-xs"
        />
        <Button size="sm" onClick={() => void copy()}>
          <Icon name="clipboard" size={15} />
          Copy
        </Button>
      </div>

      {/* Sized by the card, not the viewport: the "full" card also sits in narrow
          columns (the profile page at 1024px), where four in a row overlap. */}
      <div
        className={cn(
          'grid gap-2',
          !canShare
            ? 'grid-cols-2 @xs:grid-cols-3'
            : compact
              ? 'grid-cols-2'
              : 'grid-cols-2 @lg:grid-cols-4',
        )}
      >
        {shareLinks(data.invite_url).map((link) => (
          <ShareLink key={link.key} link={link} compact={compact} />
        ))}
        {canShare && (
          <button
            type="button"
            onClick={() => void share()}
            className={cn(
              'border-line bg-surface text-ink hover:border-brand-200 hover:bg-brand-50/60 inline-flex items-center justify-center gap-1.5 rounded-lg border font-medium shadow-xs transition',
              compact ? 'h-8 px-2 text-xs' : 'h-9 px-3 text-sm',
            )}
          >
            <Icon name="external" size={15} className="text-brand-600" />
            More
          </button>
        )}
      </div>

      <div className="border-line border-t pt-3">
        <p className="text-ink text-sm">
          <span className="font-semibold tabular-nums">{joined}</span>{' '}
          {joined === 1 ? 'friend' : 'friends'} joined
          {data.verified_count > 0 && data.verified_count < joined && (
            <span className="text-ink-muted"> · {data.verified_count} verified</span>
          )}
        </p>
        {recent.length > 0 ? (
          <ul className="divide-line mt-2 divide-y">
            {recent.map((friend, index) => (
              <li
                key={`${friend.display_name}-${friend.joined_at}-${index}`}
                className="flex items-center justify-between gap-3 py-2 text-xs"
              >
                <span className="text-ink min-w-0 truncate font-medium">{friend.display_name}</span>
                <span className="flex shrink-0 items-center gap-2">
                  {!friend.verified && <Badge tone="warning">Pending</Badge>}
                  <span className="text-ink-muted">{relativeTime(friend.joined_at)}</span>
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-ink-muted mt-1 text-xs">
            Friends who sign up with your link show up here.
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * "Invite classmates": the student's invite link, one-tap share targets and
 * who has joined through it. Tracking only -- there is no reward to promise.
 *
 * `variant="rail"` is the compact dashboard version; `"full"` is the account page section.
 */
export function InviteCard({ variant = 'full', className }) {
  const compact = variant === 'rail';
  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: ['my-referrals'],
    queryFn: referralApi.getMyReferrals,
  });

  let body;
  if (isPending) {
    body = (
      <div className="space-y-3">
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-full" />
      </div>
    );
  } else if (isError) {
    body = <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />;
  } else {
    body = <InviteBody data={data} compact={compact} />;
  }

  if (compact) {
    return (
      <RailCard
        icon="users"
        title="Invite classmates"
        subtitle="Share Join-Wonder with your friends"
        className={className}
      >
        {body}
      </RailCard>
    );
  }

  return (
    <Card className={className}>
      <CardHeader
        title="Invite classmates"
        description="Know someone looking for an internship? Send them your link."
      />
      {body}
    </Card>
  );
}
